import dayjs from 'dayjs'
// vitest からは Nuxt auto-import が効かないため明示 import に。
import { ref, readonly, watchEffect, type Ref } from 'vue'
import { mergeById } from '~/utils/merge-by-id'
import { storage } from '~/utils/session-storage'
import { requestOrThrow } from './api-client'
import { cancelable, updatable } from './booking/policy'
import { useSubProfile } from './sub-profile'
import type { GetEvent200, GetEventList200, PatchEvent200 } from '~/types/api/Event'
import type { GetEventRequest200, PostEventRequestRegister201 } from '~/types/api/EventRequest'
import type { GetHolidayList200 } from '~/types/api/Holiday'
import type { CamelCaseKeys } from '~/types/util'
import type { TimeSlot } from './booking-entry'
import type { RelatedStore } from './related-store'
// 受診者(対象者)は proxy_group_member_id で表す。Event/EventRequest 応答が
// proxy_group_member_id を提供するようになったため、生成型をそのまま使う。
export type BookingBase = CamelCaseKeys<GetEventList200>[number]
export type Booking = CamelCaseKeys<GetEvent200>
export type BookingRequest = CamelCaseKeys<GetEventRequest200>[number]

export interface EditForm {
  store?: RelatedStore
  booking?: Booking | BookingRequest
  fromAt?: Date
}

const editFormStorage = storage<EditForm>('bookingEditForm')
const editForm = ref(editFormStorage.getItem()) as Ref<EditForm>
if (editForm.value.fromAt) editForm.value.fromAt = new Date(editForm.value.fromAt)
watchEffect(() => editFormStorage.setItem(editForm.value))

const input = ({ store, booking, fromAt }: EditForm) => {
  if (store) editForm.value.store = store
  if (booking) editForm.value.booking = booking
  if (fromAt) editForm.value.fromAt = fromAt
  return readonly(editForm)
}

// 一覧 index は単一 target スコープ(target 未指定だと本人分しか返らない)。
// 本人+メンバーの予約/依頼を全て表示するため、対象者ごとに並列取得して id でマージする。
const getBookings = async () => {
  const targets = await useSubProfile().getTargets()
  const fromAt = dayjs().format('YYYY-MM-DD')
  const lists = await Promise.all(
    targets.map((target) =>
      requestOrThrow<CamelCaseKeys<GetEventList200>>(`events`, {
        query: {
          eventType: 'booking',
          fromAt,
          status: 'active',
          targetGroupMemberId: target.proxyGroupMemberId,
        },
      })
    )
  )
  return mergeById(lists)
}

const getBooking = async (id: number, targetGroupMemberId?: number) => {
  return await requestOrThrow<Booking>(`/events/${id}`, {
    query: targetGroupMemberId != null ? { targetGroupMemberId } : {},
  })
  // ^ Booking is CamelCaseKeys<GetEvent200>
}

const getBookingRequests = async () => {
  const targets = await useSubProfile().getTargets()
  const lists = await Promise.all(
    targets.map((target) =>
      requestOrThrow<CamelCaseKeys<GetEventRequest200>>(`event_requests`, {
        query: {
          status: 'active',
          targetGroupMemberId: target.proxyGroupMemberId,
        },
      })
    )
  )
  return mergeById(lists)
}

const getEventTimeSlots = async ({
  id,
  fromOn,
  toOn,
  targetGroupMemberId,
}: {
  id: number
  fromOn: Date
  toOn: Date
  targetGroupMemberId?: number
}) => {
  return (await requestOrThrow<TimeSlot>(`/events/${id}/time_slots`, {
    params: {
      target_on_from: dayjs(fromOn).format('YYYY-MM-DD'),
      target_on_to: dayjs(toOn).format('YYYY-MM-DD'),
      ...(targetGroupMemberId != null ? { target_group_member_id: targetGroupMemberId } : {}),
    },
    skipConvertKeyRecursivelyOnResponse: true,
  })) as TimeSlot
}
const getEventRequestTimeSlots = async ({
  id,
  fromOn,
  toOn,
  targetGroupMemberId,
}: {
  id: number
  fromOn: Date
  toOn: Date
  targetGroupMemberId?: number
}) => {
  return (await requestOrThrow<TimeSlot>(`/event_requests/${id}/time_slots`, {
    params: {
      target_on_from: dayjs(fromOn).format('YYYY-MM-DD'),
      target_on_to: dayjs(toOn).format('YYYY-MM-DD'),
      ...(targetGroupMemberId != null ? { target_group_member_id: targetGroupMemberId } : {}),
    },
    skipConvertKeyRecursivelyOnResponse: true,
  })) as TimeSlot
}

const getHolidays = async ({ fromAt, toAt }: { fromAt: Date; toAt: Date }) => {
  return await requestOrThrow<CamelCaseKeys<GetHolidayList200>>(`/holidays`, {
    params: {
      from_at: dayjs(fromAt).format('YYYY-MM-DD'),
      to_at: dayjs(toAt).format('YYYY-MM-DD'),
    },
  })
}

const updateBooking = async () => {
  const fromAt = editForm.value.fromAt!.toISOString()
  const toAt = dayjs(fromAt).add(editForm.value.booking!.durationByMinutes, 'minute').toISOString()
  const targetGroupMemberId = editForm.value.booking!.proxyGroupMemberId

  return await requestOrThrow<CamelCaseKeys<PatchEvent200>>(`/events/${editForm.value.booking!.id}`, {
    method: 'PATCH',
    query: targetGroupMemberId != null ? { targetGroupMemberId } : {},
    body: {
      fromAt,
      toAt,
    },
  })
}

const cancelBooking = async (id: number, targetGroupMemberId?: number) => {
  return await requestOrThrow<CamelCaseKeys<PatchEvent200>>(`/events/${id}`, {
    method: 'PATCH',
    query: targetGroupMemberId != null ? { targetGroupMemberId } : {},
    body: {
      status: 'canceled',
    },
  })
}

const registerBookingRequest = async () => {
  const fromAt = editForm.value.fromAt!.toISOString()
  const toAt = dayjs(fromAt).add(editForm.value.booking!.durationByMinutes, 'minute').toISOString()
  // 予約依頼の register は target 必須(未指定だと本人名義で誤登録される)。
  const targetGroupMemberId = editForm.value.booking!.proxyGroupMemberId

  return await requestOrThrow<CamelCaseKeys<PostEventRequestRegister201>>(
    `/event_requests/${editForm.value.booking!.id}/register`,
    {
      method: 'POST',
      query: targetGroupMemberId != null ? { targetGroupMemberId } : {},
      body: {
        fromAt,
        toAt,
      },
    }
  )
}

const resetEditForm = () => {
  editForm.value = {}
}

export const useBooking = () => {
  return {
    editForm: readonly(editForm) as Readonly<Ref<EditForm>>,
    input,
    getBookings,
    getBooking,
    getBookingRequests,
    getEventTimeSlots,
    getEventRequestTimeSlots,
    getHolidays,
    updateBooking,
    cancelBooking,
    registerBookingRequest,
    resetEditForm,
    cancelable,
    updatable,
  }
}
